import { Slide } from '@progress/kendo-react-animation';
import './InvoiceDetailsPage.scss'
import { Suspense, useState } from 'react';
import _ from 'lodash';
import { faFileInvoice } from '@fortawesome/pro-regular-svg-icons';

import {
    AdjustmentGridView,
    AdjustmentSummary,
    ExportButton,
    InvoiceDetailHeader,
    InvoiceDetailSidebar,
    LoaderOverlay,
    SuspenseBoundary,
    FileList,
    Icon
} from 'components';
import { AdjustmentTypeMap, InvoiceCategories } from 'config';
import { AdjustmentType } from 'enums';
import { formatfileNameDate } from 'functions';
import { useBackupDocument, useInvoiceParams, useSelectedCategory } from 'hooks';
import DocumentErrorDialog from 'hooks/DocumentErrorDialog';
import { useInvoice, useInvoiceDetails, useInvoiceDocuments, useRefreshInvoiceDetails } from 'state';
import { InvoiceDetail, InvoiceDetailHeaderData, InvoiceSummary } from 'types';

export default function InvoiceDetailsPage() {
    const { accountId, vendorId, invoiceId, voucherId } = useInvoiceParams();
    const refresh = useRefreshInvoiceDetails(accountId, vendorId, invoiceId, voucherId);

    return (
        <SuspenseBoundary
            errorMessage={`Error retrieving invoice details for invoice ${invoiceId}`}
            onReset={refresh}
        >
            <InvoiceDetailsComponent />
        </SuspenseBoundary>
    );
};

const InvoiceDetailsComponent = () => {
    let { accountId, vendorId, invoiceId, voucherId, adjustmentIdentityKey } = useInvoiceParams();

    const invoice = useInvoice(accountId, vendorId, invoiceId, voucherId, adjustmentIdentityKey) as InvoiceSummary;
    const invoiceDetails = useInvoiceDetails(accountId, vendorId, invoiceId, voucherId);
    const [selectedDetails, setSelectedDetails] = useState<InvoiceDetail[]>([]);
    const [expanded, setExpanded] = useState<boolean>(false);

    const {
        gridProps,
        selectCategory,
        selectedCategory
    } = useSelectedCategory(invoiceDetails, AdjustmentTypeMap, AdjustmentType.Pricing, () => setSelectedDetails([]));

    const fileDate: string = formatfileNameDate(new Date());
    const exportFileName: string = `InvoiceDetails_${invoiceId}_${fileDate}.xlsx`;

    const getDetailHeader = () => {
        const getDiscountAmount = () => {
            return invoiceDetails.filter(detail => { return detail.adjustmentTypeId === AdjustmentType.Discount })
                .reduce((sum, cur) => { return sum + cur.adjustmentAmount }, 0);
        };
        const discountAmount: number = getDiscountAmount();

        const detailHeader: InvoiceDetailHeaderData = {
            invoiceId: invoice?.invoiceId ?? "",
            paidDate: invoice?.paymentDate,
            invoicedAmount: invoice?.userVoucherDecimal || 0,
            discountAmount: discountAmount,
            adjAmount: (invoice && invoice.dotPriceAdjustment + invoice.dotQuantityAdjustment) || 0,
            paidAmount: invoice?.invoicePaidAmount || 0,
            scheduledPayDate: invoice?.scheduledPayDate,
            paymentHoldFlag: invoice?.paymentHoldFlag,
            voucherInApReview: invoice?.voucherInApReview,
            voucherPoId: invoice?.voucherPoId || "N/A",
            paymentIdRef: Number(invoice?.paymentIdRef),
            vendorId: invoice?.vendorId,
        }
        return detailHeader
    };

    const invoiceDetailHeader: InvoiceDetailHeaderData = getDetailHeader();
    const invoiceDocuments = useInvoiceDocuments(accountId, invoice?.vendorId ?? '', invoice?.invoiceId ?? '', invoice?.voucherPoId ?? '');
    const getInvoiceDocument = useBackupDocument();
    const disabled = !invoice?.paymentDate;

    return (
        <div className="details-page">
            <div className='details-page-title'>
                <span>
                    <Icon icon={faFileInvoice}/> 
                </span>
                <span>
                    Invoice Details
                </span>
            </div>
            <div className="details-page-main">
                <div className="details-page-main-header">
                    <InvoiceDetailHeader
                        invoiceDetailHeader={invoiceDetailHeader}
                        invoice={invoice}
                    /> 
                    <div className='details-page-main-header-export'>
                        {invoiceDetails.length > 0 && <ExportButton data={invoiceDetails} fileName={exportFileName}/>}
                    </div>
                </div>
                <div className="details-page-main-container">
                    <div className="details-page-main-container-summary">
                        <div className="details-page-main-container-summary-categories">
                            <AdjustmentSummary
                                adjustments={invoiceDetails}
                                categories={InvoiceCategories}
                                selectedCategory={selectedCategory}
                                selectCategory={selectCategory}
                            />
                        </div>
                        <div className={"documents-container"}>
                            <div className={"documents-heading"}>
                                <span>Backup Documentation</span>
                            </div>
                            <Suspense fallback={<LoaderOverlay isLoading />}>
                                {_.isEmpty(invoiceDocuments)
                                    ? <span className={"documents-none"}>No documents found</span>
                                    : <>
                                        <FileList documents={invoiceDocuments} onClick={getInvoiceDocument} />
                                        {getInvoiceDocument.showError && <DocumentErrorDialog onClose={() => getInvoiceDocument.setShowError(false)} documentIdentifier={getInvoiceDocument.errorIdentifier} />}
                                      </>
                                }
                            </Suspense> 
                        </div>
                    </div>
                    <div className="details-page-main-container-grid-container">
                        <AdjustmentGridView
                            {...gridProps}
                            selectedDetails={selectedDetails ?? []}
                            setSelectedDetails={setSelectedDetails}
                            setExpanded={setExpanded}
                            selectMode={gridProps.selectMode || 'multiple'}
                            disabled={disabled}
                            context="gridView"
                        />
                    </div>
                </div>
            </div>
            <div>
                <Slide
                    appear={expanded}
                    className={`details-page-sidebar sidebar-${selectedDetails?.length === 1 ? "selected" : "unselected"}`}
                    direction={"left"}
                >
                    {expanded && selectedDetails?.length === 1 ?
                        <div className="details-page-sidebar-content">
                            <InvoiceDetailSidebar
                                expanded={expanded}
                                selectedDetail={selectedDetails[0]}
                                setSelectedDetails={setSelectedDetails}
                                setExpanded={setExpanded}
                                disableDisputeButton={!invoice.paymentDate}
                            />
                        </div>
                        : null
                    }
                </Slide>
            </div>
        </div>
    );
};
